import React, { createContext, type ReactNode, useEffect, useReducer, useContext } from 'react';
import chunk from 'lodash/chunk';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import maxBy from 'lodash/maxBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import min from 'date-fns/min';
import { fetchQuery, graphql, type Environment, useRelayEnvironment } from 'react-relay';
import { ff, getFeatureFlagValue } from '@atlassian/jira-feature-flagging';
import { asColor } from '@atlassian/jira-issue-epic-color';
import { createAri } from '@atlassian/jira-platform-ari';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { mainDeploymentsAGSQuery$data } from '@atlassian/jira-relay/src/__generated__/mainDeploymentsAGSQuery.graphql';
import type {
	mainDeploymentsQuery$data,
	mainDeploymentsQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/mainDeploymentsQuery.graphql';
import type { mainIssueAGSQuery$data } from '@atlassian/jira-relay/src/__generated__/mainIssueAGSQuery.graphql';
import type { mainIssueQuery$data } from '@atlassian/jira-relay/src/__generated__/mainIssueQuery.graphql';
import {
	type CloudId,
	toAccountId,
	toIssueId,
	toIssueKey,
	toProjectId,
	toIssueTypeId,
	type ProjectId,
	type IssueId,
} from '@atlassian/jira-shared-types';
import {
	type LoadingState,
	LoadingStateOptions,
	type ServiceQueryResultWithStatus,
} from '@atlassian/jira-software-deployments-service-query-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	issuesAndDeploymentsFetched,
	issuesFetched,
	deploymentsFetched,
	deploymentsAGSFetched,
	deploymentPageFetched,
	issuesAGSFetched,
} from '../../common/graphql/metrics';
import {
	type Issue,
	type EpicLozenge,
	type Deployment,
	DeploymentEnvironmentTypeOptions,
	DeploymentStateOptions,
	type DeploymentEnvironment,
	type DeploymentState,
	type DeploymentPipeline,
} from '../../common/types';
import { getResourceId } from '../../common/utils/ari';
import { getOrderedDeploymentsByTime } from '../../common/utils/deployments';
import { useProjectId } from '../get-project';
import {
	MAXIMUM_FETCHABLE_ISSUES,
	MAXIMUM_ISSUES_PER_DEPLOYMENT_REQUEST,
	MAXIMUM_DEPLOYMENT_PAGES_TO_FETCH_PER_ISSUE_PAGE,
	DEPLOYMENTS_PAGE_SIZE,
	MAXIMUM_FETCHABLE_DEPLOYMENTS,
	ISSUE_PAGE_SIZE,
} from './constants';
import type {
	CoreData,
	CoreDataActions,
	CoreDataResult,
	CoreDataState,
	FetchCoreDataResult,
	Filter,
	IssueFilter,
	DeploymentFilter,
	UseIssuesResult,
	IssuePage,
	TimeWindowFilter,
} from './types';
import { isFilterApplied } from './utils/analytics';
import { coreDataReducer, initialState } from './utils/core-data-provider-state';
import { isFilterSuitableForAGSQuery } from './utils/filter';
import { sortIssueByLatestDeploymentsAndLocateEmptyDeploymentsLast } from './utils/issue-transformers';
import {
	getIssueQueryVariables,
	getDeploymentQueryVariables,
	getNextDeploymentPageCursor,
	getNextIssuePageCursor,
	groupDeploymentsByIssueId,
	transformEnvironmentType,
	getNextDeploymentAGSPageCursor,
} from './utils/pagination';
import {
	collectorFromNumIssuesDeployments,
	collectorFromNumIssues,
	isInitialLoad,
	collectorFromCoreData,
	setInitialLoadFinished,
} from './utils/performance-monitor';

// @ts-expect-error - TS7031 - Binding element 'summary' implicitly has an 'any' type. | TS7031 - Binding element 'color' implicitly has an 'any' type.
const transformEpic = ({ summary, color }): EpicLozenge => {
	if (summary == null) {
		throw Error('Invalid issue epic');
	}
	return {
		summary,
		color: asColor(color === undefined ? null : color),
	};
};

type IssueSearchStableQueryData = NonNullable<
	NonNullable<
		NonNullable<NonNullable<mainIssueAGSQuery$data['jira']>['issueSearchStable']>['edges']
	>[0]
>;

const transformIssueSearch = ({ node }: IssueSearchStableQueryData): Issue => {
	if (node == null) {
		throw Error('Invalid Issue Data');
	}

	const { fieldsByIdOrAlias: fields, id: issueId, key: issueKey } = node;

	if (issueId == null || issueKey == null || fields == null) {
		throw Error('Invalid Issue Data');
	}

	const issueTypeId = fields.find((field) => field?.fieldId === 'issuetype')?.issueType?.id ?? null;

	if (issueTypeId == null) {
		throw Error('Invalid Issue Data');
	}

	const projectId = '100001'; // TODO

	const summary = fields.find((field) => field?.fieldId === 'summary')?.text ?? '';
	const assigneeUserValue = fields.find((field) => field?.fieldId === 'assignee')?.user ?? null;

	let assignee = null;
	if (assigneeUserValue) {
		const { accountId, name, picture } = assigneeUserValue;

		if (accountId != null && name != null) {
			assignee = {
				id: toAccountId(accountId),
				displayName: name,
				avatarUrl: picture != null ? picture : undefined,
			};
		}
	}

	const parent = fields.find((field) => field?.fieldId === 'parent') ?? null;
	const epicSummary =
		parent?.parentIssue?.fieldsByIdOrAlias?.find((field) => field?.fieldId === 'summary')?.text ??
		null;
	const epicColor = parent?.parentIssue?.issueColorField?.color?.colorKey;
	const epic =
		parent?.parentIssue == null
			? null
			: transformEpic({
					summary: epicSummary,
					color: epicColor,
				});

	return {
		id: toIssueId(getResourceId(issueId)),
		key: toIssueKey(issueKey),
		typeId: toIssueTypeId(getResourceId(issueTypeId)),
		projectId: toProjectId(projectId),
		summary,
		assignee,
		epic,
	};
};

// @ts-expect-error - TS7006 - Parameter 'edge' implicitly has an 'any' type.
const transformIssue = (edge): Issue => {
	const issueId = edge?.node?.id;
	const issueKey = edge?.node?.issueKey;
	const issueTypeId = edge?.node?.issueTypeId;
	const projectId = '100001'; // TODO
	const summary = edge?.node?.summary ?? '';
	const epic = edge?.node?.epic ?? null;
	const assigneeUserValue = edge?.node?.assignee ?? null;

	let assignee = null;
	if (assigneeUserValue) {
		const { accountId, name, picture } = assigneeUserValue;

		if (accountId != null && name != null) {
			assignee = {
				id: toAccountId(accountId),
				displayName: name,
				avatarUrl: picture != null ? picture : undefined,
			};
		}
	}

	if (issueId == null || issueKey == null || issueTypeId == null || projectId == null) {
		throw Error('Invalid Issue Data');
	}

	return {
		id: toIssueId(getResourceId(issueId)),
		key: toIssueKey(issueKey),
		typeId: toIssueTypeId(getResourceId(issueTypeId)),
		projectId: toProjectId(projectId),
		summary,
		assignee,
		epic: epic == null ? null : transformEpic(epic),
	};
};

export const transformIssueAGSQueryResult = (
	data?: mainIssueAGSQuery$data,
	error?: Error,
): ServiceQueryResultWithStatus<IssuePage> => {
	const loadingState = LoadingStateOptions.READY;

	if (data?.jira?.issueSearchStable?.edges == null) {
		return {
			loadingState: LoadingStateOptions.HAS_ERROR,
			error:
				error ||
				Error(
					'Critical data missing from "mainIssueQuery" response. Check Apollo Studio for details.',
				),
		};
	}

	try {
		return {
			loadingState,
			data: {
				issues: data.jira.issueSearchStable.edges.filter(Boolean).map(transformIssueSearch),
				pageInfo: { nextPageCursor: null },
			},
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (issueTransformError: any) {
		return {
			loadingState: LoadingStateOptions.HAS_ERROR,
			error: issueTransformError,
		};
	}
};

export const transformIssueQueryResult = (
	data?: mainIssueQuery$data,
	error?: Error,
): ServiceQueryResultWithStatus<IssuePage> => {
	const loadingState = LoadingStateOptions.READY;

	if (data?.jiraReleases?.issues?.edges == null) {
		return {
			loadingState: LoadingStateOptions.HAS_ERROR,
			error:
				error ||
				Error(
					'Critical data missing from "mainIssueQuery" response. Check Apollo Studio for details.',
				),
		};
	}

	const nextPageCursor =
		data.jiraReleases.issues.pageInfo.hasNextPage === true
			? data.jiraReleases.issues.edges[data.jiraReleases.issues.edges.length - 1]?.cursor ?? null
			: null;

	try {
		return {
			loadingState,
			data: {
				issues: data.jiraReleases.issues.edges.map(transformIssue),
				pageInfo: {
					nextPageCursor,
				},
			},
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (issueTransformError: any) {
		return {
			loadingState: LoadingStateOptions.HAS_ERROR,
			error: issueTransformError,
		};
	}
};

type CoreDataFilterProviderProps = {
	filter: Filter | null;
	children: ReactNode;
};

// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
export const CoreDataContext = createContext<CoreDataResult | undefined>();

export const CoreDataFilterContext = createContext<Filter | null>(null);

export const CoreDataFilterProvider = ({ filter, children }: CoreDataFilterProviderProps) => (
	<CoreDataFilterContext.Provider value={filter}>{children}</CoreDataFilterContext.Provider>
);

export const useCoreDataFilter = (): Filter | null => useContext(CoreDataFilterContext);

const useCoreDataContext = () => {
	const result = useContext(CoreDataContext);

	if (result === undefined) {
		throw new Error('Missing core data service provider');
	}

	return result;
};

export const useCoreData = (): CoreDataResult => useCoreDataContext();

type CoreDataLimits = {
	hasReachedMaxDeploymentsLimit: boolean;
	hasReachedMaxIssuesLimit: boolean;
};

export const useCoreDataLimits = (): CoreDataLimits => {
	const a = useCoreData();

	const { hasReachedMaxDeploymentsLimit, hasReachedMaxIssuesLimit } = a;
	return { hasReachedMaxDeploymentsLimit, hasReachedMaxIssuesLimit };
};

export const useIssues = (): UseIssuesResult => {
	const result = useCoreData();

	return {
		...result.serviceResult,
		data: result.serviceResult.data?.issues ?? [],
		hasReachedMaxIssuesLimit: result.hasReachedMaxIssuesLimit,
		getNextIssuePage: result.getNextIssuePage,
	};
};

export const useIssue = (issueId: IssueId): Issue | undefined => {
	const result = useCoreData();
	return result.serviceResult.data?.issuesByIssueId[issueId] ?? undefined;
};

export const useAllDeployments = (): ServiceQueryResultWithStatus<Deployment[]> => {
	const result = useCoreData();

	return {
		...result.serviceResult,
		data: result.serviceResult.data?.allDeployments ?? [],
	};
};

export const useIssueDeployments = (
	issueId: IssueId,
): ServiceQueryResultWithStatus<Deployment[]> => {
	const result = useCoreData();

	return {
		...result.serviceResult,
		data: result.serviceResult.data?.deploymentsByIssueId[issueId] ?? [],
	};
};

export const useIsCoreLoadingByFilterChange = (): boolean => {
	const result = useCoreData();
	return result.serviceResult.loadingState === LoadingStateOptions.LOADING_SET_VARIABLES;
};

export const useIsLoadingNextPage = (): boolean => {
	const result = useCoreData();
	return result.serviceResult.loadingState === LoadingStateOptions.LOADING_NEXT_PAGE;
};

const { HAS_ERROR } = LoadingStateOptions;

type FetchIssuesResult = {
	issues: Issue[];
	hasNextPage: boolean;
};

const fetchIssuesAGSQuery = async (
	jqlSearch: string,
	environment: Environment,
	cloudId: CloudId,
) => {
	let data;
	let error;
	try {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		data = (await fetchQuery(
			environment,
			graphql`
				query mainIssueAGSQuery($first: Int!, $jqlSearch: String!, $cloudId: ID!) {
					jira {
						issueSearchStable(
							first: $first
							issueSearchInput: { jql: $jqlSearch }
							cloudId: $cloudId
						) {
							edges {
								node {
									id
									key
									fieldsByIdOrAlias(
										idsOrAliases: ["issuetype", "assignee", "parent", "summary"]
										ignoreMissingFields: true
									) @optIn(to: "JiraIssueFieldsByIdOrAlias") {
										... on JiraIssueTypeField {
											fieldId
											issueType {
												id
											}
										}
										... on JiraSingleLineTextField {
											fieldId
											text
										}
										... on JiraSingleSelectUserPickerField {
											fieldId
											user {
												__typename
												accountId
												name
												picture
											}
										}
										... on JiraParentIssueField {
											fieldId
											parentIssue {
												id
												issueColorField {
													color {
														colorKey
													}
												}
												fieldsByIdOrAlias(idsOrAliases: ["summary"])
													@optIn(to: "JiraIssueFieldsByIdOrAlias") {
													... on JiraSingleLineTextField {
														fieldId
														text
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				first: ISSUE_PAGE_SIZE,
				cloudId,
				jqlSearch,
			},
			{ fetchPolicy: 'store-or-network' },
		).toPromise()) as mainIssueAGSQuery$data;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		error = e;
	}

	return {
		data,
		error,
	};
};

export const fetchIssuesAGS = async (
	environment: Environment,
	numIssuePagesToFetch: number,
	issueIds: string[],
	cloudId: CloudId,
	projectId: ProjectId,
): Promise<FetchIssuesResult> => {
	let issues: Issue[] = [];
	let currentIssuePage = 0;
	if (isInitialLoad()) {
		issuesAGSFetched.start();
	}

	// We will fetch ISSUE_PAGE_SIZE issues at a time by building a JQL string
	// to search for issue ids
	const issueIdPagesToFetch = chunk(issueIds, ISSUE_PAGE_SIZE);

	do {
		const jqlSearch = `project = ${projectId} AND id IN (${issueIdPagesToFetch[currentIssuePage].join(',')})`;

		// eslint-disable-next-line no-await-in-loop
		const { data, error } = await fetchIssuesAGSQuery(jqlSearch, environment, cloudId);

		const issuePageResponse = transformIssueAGSQueryResult(data, error);
		if (issuePageResponse.error || issuePageResponse.data == null) {
			throw new Error(
				`issuePageResponse has no data, we should never get here. error: ${issuePageResponse.error ?? 'unknown'}`,
			);
		}

		issues = issues.concat(issuePageResponse.data.issues);
		currentIssuePage += 1;
	} while (
		currentIssuePage < issueIdPagesToFetch.length &&
		currentIssuePage < numIssuePagesToFetch
	);

	if (isInitialLoad()) {
		issuesAGSFetched.stop({
			customData: {
				...collectorFromNumIssues({
					numIssues: issues.length,
					issuePageSize: ISSUE_PAGE_SIZE,
					deploymentsPageSize: DEPLOYMENTS_PAGE_SIZE,
				}),
				relay: true,
			},
		});
	}

	return {
		issues,
		hasNextPage: currentIssuePage < issueIdPagesToFetch.length,
	};
};

// @ExportForTesting
export const fetchIssues = async (
	environment: Environment,
	filter: IssueFilter,
	totalIssuePageNum: number,
	cloudId: CloudId,
): Promise<FetchIssuesResult | null> => {
	let issues: Issue[] = [];
	let issueAfterCursor = null;
	let totalIssuePageCount = 0;
	if (isInitialLoad()) {
		issuesFetched.start();
	}
	do {
		let data;
		let error;

		try {
			// eslint-disable-next-line no-await-in-loop
			data = await fetchQuery(
				environment,
				graphql`
					query mainIssueQuery($first: Int!, $after: String, $filter: JiraReleasesIssueFilter!) {
						jiraReleases {
							issues(first: $first, after: $after, filter: $filter) {
								pageInfo {
									hasNextPage
								}
								edges {
									cursor
									node {
										id
										issueKey
										issueTypeId
										assignee {
											__typename
											accountId
											name
											picture
										}
										epic {
											id
											summary
											color
										}
										summary
									}
								}
							}
						}
					}
				`,
				{
					...getIssueQueryVariables(filter, cloudId, issueAfterCursor),
				},
				{ fetchPolicy: 'store-or-network' },
			).toPromise();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			error = e;
		}

		// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'mainIssueQuery$data | undefined'.
		const issuePageResponse = transformIssueQueryResult(data, error);
		if (issuePageResponse.error) {
			throw issuePageResponse.error;
		}
		if (issuePageResponse.data == null) {
			throw new Error('issuePageResponse has no data, we should never get here');
		}
		const { issues: curPageIssues } = issuePageResponse.data;

		issues = issues.concat(curPageIssues);
		// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'mainIssueQuery$data | undefined'.
		issueAfterCursor = getNextIssuePageCursor(data);
		totalIssuePageCount += 1;
	} while (totalIssuePageCount < totalIssuePageNum && issueAfterCursor !== null);
	if (isInitialLoad()) {
		issuesFetched.stop({
			customData: {
				...collectorFromNumIssues({
					numIssues: issues.length,
					issuePageSize: ISSUE_PAGE_SIZE,
					deploymentsPageSize: DEPLOYMENTS_PAGE_SIZE,
				}),
				relay: true,
			},
		});
	}

	return {
		issues,
		hasNextPage: issueAfterCursor !== null,
	};
};

const transformDeploymentState = (state: string | DeploymentState): DeploymentState => {
	switch (state) {
		case 'PENDING':
			return DeploymentStateOptions.PENDING;
		case 'IN_PROGRESS':
			return DeploymentStateOptions.IN_PROGRESS;
		case 'SUCCESSFUL':
			return DeploymentStateOptions.SUCCESSFUL;
		case 'CANCELLED':
			return DeploymentStateOptions.CANCELLED;
		case 'FAILED':
			return DeploymentStateOptions.FAILED;
		case 'ROLLED_BACK':
			return DeploymentStateOptions.ROLLED_BACK;
		case 'UNKNOWN':
			return DeploymentStateOptions.UNKNOWN;
		default:
			return DeploymentStateOptions.UNKNOWN;
	}
};

const transformPipeline = (pipeline: {
	readonly displayName: string | null | undefined;
	readonly url: string | null | undefined;
}): DeploymentPipeline => {
	const { displayName, url } = pipeline;
	if (displayName == null || url == null) {
		throw new Error('pipeline not found');
	}
	return {
		id: 'pipelineId', // TODO remove once we stop supporting two core-data services
		displayName,
		url,
	};
};

const transformEnvironment = (environment: {
	// @ts-expect-error - TS2304 - Cannot find name 'DevOpsEnvironmentCategory'.
	readonly category: DevOpsEnvironmentCategory | null | undefined;
	readonly displayName: string | null | undefined;
}): DeploymentEnvironment => {
	const { displayName, category } = environment;
	if (displayName == null) {
		throw new Error('environment not found');
	}
	return {
		id: 'environmentId', // TODO remove once we stop supporting two core-data services
		type:
			category == null
				? DeploymentEnvironmentTypeOptions.UNMAPPED
				: transformEnvironmentType(category),
		displayName,
	};
};

const transformDeployment = ({
	// @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
	id,
	// @ts-expect-error - TS7031 - Binding element 'issueIds' implicitly has an 'any' type.
	issueIds,
	// @ts-expect-error - TS7031 - Binding element 'deploymentSequenceNumber' implicitly has an 'any' type.
	deploymentSequenceNumber,
	// @ts-expect-error - TS7031 - Binding element 'updateSequenceNumber' implicitly has an 'any' type.
	updateSequenceNumber,
	// @ts-expect-error - TS7031 - Binding element 'displayName' implicitly has an 'any' type.
	displayName,
	// @ts-expect-error - TS7031 - Binding element 'url' implicitly has an 'any' type.
	url,
	// @ts-expect-error - TS7031 - Binding element 'description' implicitly has an 'any' type.
	description,
	// @ts-expect-error - TS7031 - Binding element 'lastUpdated' implicitly has an 'any' type.
	lastUpdated,
	// @ts-expect-error - TS7031 - Binding element 'state' implicitly has an 'any' type.
	state,
	// @ts-expect-error - TS7031 - Binding element 'pipeline' implicitly has an 'any' type.
	pipeline,
	// @ts-expect-error - TS7031 - Binding element 'environment' implicitly has an 'any' type.
	environment,
}: {}): Deployment => {
	if (
		issueIds == null ||
		deploymentSequenceNumber == null ||
		updateSequenceNumber == null ||
		displayName == null ||
		url == null ||
		description == null ||
		lastUpdated == null ||
		state == null ||
		pipeline == null ||
		environment == null
	) {
		throw Error('Invalid deployment');
	}
	return {
		id,
		// @ts-expect-error - TS7006 - Parameter 'issueId' implicitly has an 'any' type.
		issueIds: issueIds.map((issueId) => toIssueId(getResourceId(issueId))),
		deploymentSequenceNumber,
		updateSequenceNumber,
		displayName,
		url,
		description,
		lastUpdated: new Date(lastUpdated),
		state: transformDeploymentState(state),
		pipeline: transformPipeline(pipeline),
		environment: transformEnvironment(environment),
	};
};

const groupByDeploymentSequenceNumber = (deployments: Deployment[]): Deployment[][] =>
	Object.values(
		groupBy(
			deployments,
			(deployment) =>
				`${deployment.deploymentSequenceNumber}_${deployment.pipeline.url}_${deployment.environment.type}_${deployment.environment.displayName}`,
		),
	);

const getByMostRecentUpdateSequenceNumber = (deployments: Deployment[]): Deployment =>
	// @ts-expect-error - TS2322 - Type 'Deployment | undefined' is not assignable to type 'Deployment'.
	maxBy(deployments, (deployment) => deployment.updateSequenceNumber);

// @ExportForTesting
export const filterForMostRecentUpdates = (deployments: Deployment[]): Deployment[] =>
	groupByDeploymentSequenceNumber(deployments).map(getByMostRecentUpdateSequenceNumber);

export const transformDeploymentAGSQueryResult = (
	data?: mainDeploymentsAGSQuery$data,
	error?: Error,
): ServiceQueryResultWithStatus<Deployment[]> => {
	if (data?.graphStore?.projectAssociatedDeployment?.edges == null) {
		return {
			loadingState: LoadingStateOptions.HAS_ERROR,
			error: error || Error('Data missing error.'),
		};
	}

	const { edges } = data.graphStore.projectAssociatedDeployment;
	const deployments = edges.flatMap((edge) => (edge?.node ? [transformDeployment(edge.node)] : []));

	return {
		loadingState: LoadingStateOptions.READY,
		data: deployments,
	};
};

// @ExportForTesting
export const transformDeploymentQueryResult = (
	data?: mainDeploymentsQuery$data,
	error?: Error,
): ServiceQueryResultWithStatus<Deployment[]> => {
	const loadingState = LoadingStateOptions.READY;

	if (data?.jiraReleases?.deployments?.edges == null) {
		return {
			loadingState,
			error:
				error ||
				Error(
					'Critical data missing from "mainDeploymentsQuery" response. Check Apollo Studio for details.',
				),
		};
	}

	const { edges } = data.jiraReleases.deployments;
	const deployments = edges.flatMap((edge) => (edge?.node ? [transformDeployment(edge.node)] : []));
	const filteredDeployments = filterForMostRecentUpdates(deployments);

	return {
		loadingState: LoadingStateOptions.READY,
		data: filteredDeployments,
	};
};

const fetchAssociatedDeploymentsQuery = async (
	environment: Environment,
	projectId: ProjectId,
	cloudId: CloudId,
	timeWindow: TimeWindowFilter,
	after: string | null,
) => {
	let data;
	let error;
	try {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		data = (await fetchQuery(
			environment,
			graphql`
				query mainDeploymentsAGSQuery(
					$projectId: ID!
					$timeAfter: Long!
					$timeBefore: Long!
					$first: Int!
					$after: String
				) {
					graphStore @optIn(to: "GraphStore") {
						projectAssociatedDeployment(
							id: $projectId
							sort: { to_deploymentLastUpdated: { direction: DESC, priority: 1 } }
							first: $first
							after: $after
							filter: {
								and: [
									{ to_deploymentLastUpdated: { greaterThanOrEqual: $timeAfter } }
									{ to_deploymentLastUpdated: { lessThan: $timeBefore } }
								]
							}
						) @optIn(to: "GraphStoreProjectAssociatedDeployment") {
							pageInfo {
								hasNextPage
								endCursor
							}
							edges {
								node {
									... on DeploymentSummary {
										id
										issueIds(projectId: $projectId)
										deploymentSequenceNumber
										updateSequenceNumber
										displayName
										url
										description
										state
										lastUpdated
										pipeline {
											url
											displayName
										}
										environment {
											category
											displayName
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				projectId: createAri({
					resourceOwner: 'jira',
					cloudId,
					resourceType: 'project',
					resourceId: projectId,
				}),
				timeBefore: timeWindow.before.getTime(),
				timeAfter: timeWindow.after.getTime(),
				first: Number(
					getFeatureFlagValue('deployment-timeline-ags-query-pagesize', DEPLOYMENTS_PAGE_SIZE),
				),
				after,
			},
			// TODO: I don't think this fetch policy is working as expected? I'm seeing the same deployments query
			// hit the network multiple times :|
			{ fetchPolicy: 'store-or-network' },
		).toPromise()) as mainDeploymentsAGSQuery$data;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		error = e;
	}
	return { data, error };
};

export const fetchDeploymentsAGS = async (
	environment: Environment,
	projectId: ProjectId,
	cloudId: CloudId,
	totalIssuePageNum: number,
	filter: Filter,
) => {
	if (isInitialLoad()) {
		deploymentsAGSFetched.start();
	}

	let allIssueIds: string[] = [];
	let allDeployments: Deployment[] = [];

	let deploymentsAfterCursor = null;
	let hasNextPage = false;
	let hasReachedMaxDeploymentsLimit = false;

	const numIssuesToFetch = totalIssuePageNum * ISSUE_PAGE_SIZE;
	do {
		// eslint-disable-next-line no-await-in-loop
		const { data, error } = await fetchAssociatedDeploymentsQuery(
			environment,
			projectId,
			cloudId,
			filter.timeWindow,
			deploymentsAfterCursor,
		);

		const {
			loadingState,
			data: deployments,
			error: fetchError,
		} = transformDeploymentAGSQueryResult(data, error);

		if (loadingState === HAS_ERROR || deployments == null) {
			throw new Error(
				`Fetching deployment failed when fetching deployments. Detail: ${
					fetchError?.message ?? 'unknown'
				}`,
			);
		}
		const issueIds = deployments.flatMap((deployment) => deployment.issueIds);

		allDeployments = allDeployments.concat(deployments);
		allIssueIds = uniq(allIssueIds.concat(issueIds));

		deploymentsAfterCursor = getNextDeploymentAGSPageCursor(data);
		hasNextPage = data?.graphStore?.projectAssociatedDeployment?.pageInfo.hasNextPage ?? false;

		if (allDeployments.length >= MAXIMUM_FETCHABLE_DEPLOYMENTS && hasNextPage) {
			hasReachedMaxDeploymentsLimit = true;
		}

		/**
		 * Keep trying to fetch deployments until we have either
		 *
		 * - At least (ISSUE_PAGE_SIZE * pageNumber) unique issues associated with deployments OR
		 * - We have run out of deployments OR
		 * - We have reached the max deployments limit
		 */
	} while (
		allIssueIds.length < numIssuesToFetch &&
		deploymentsAfterCursor != null &&
		!hasReachedMaxDeploymentsLimit
	);

	if (isInitialLoad()) {
		deploymentsAGSFetched.stop();
	}

	return {
		deployments: allDeployments,
		issueIds: allIssueIds,
		hasNextPage,
		hasReachedMaxDeploymentsLimit,
	};
};

type FetchDeploymentsResult = {
	deployments: Deployment[];
	hasReachedMaxDeploymentsLimit: boolean;
};
// @ExportForTesting
export const fetchDeployments = async (
	environment: Environment,
	filter: DeploymentFilter,
	cloudId: CloudId,
): Promise<FetchDeploymentsResult | null> => {
	const { issueIds } = filter;
	let deployments: Deployment[] = [];
	let deploymentAfterCursor = null;
	let hasReachedMaxDeploymentsLimit = false;

	if (isInitialLoad()) {
		deploymentsFetched.start();
	}

	// Data depot has limit for the number of issues. We have to cut by issue pages.
	for (let i = 0; i < issueIds.length; i += MAXIMUM_ISSUES_PER_DEPLOYMENT_REQUEST) {
		let deploymentsPageNumber = 0;
		do {
			deploymentsPageNumber += 1;
			const lastIndex =
				i + ISSUE_PAGE_SIZE > issueIds.length ? issueIds.length : i + ISSUE_PAGE_SIZE;
			const partialIssueAris = issueIds.slice(i, lastIndex);

			if (isInitialLoad()) {
				deploymentPageFetched.start();
			}

			const variables: mainDeploymentsQuery$variables = getDeploymentQueryVariables(
				{ ...filter, issueIds: partialIssueAris },
				cloudId,
				deploymentAfterCursor,
			);

			let data;
			let error;

			try {
				// eslint-disable-next-line no-await-in-loop
				data = await fetchQuery(
					environment,
					graphql`
						query mainDeploymentsQuery(
							$first: Int!
							$after: String
							$filter: JiraReleasesDeploymentFilter!
						) {
							jiraReleases {
								deployments(first: $first, after: $after, filter: $filter) {
									pageInfo {
										hasNextPage
									}
									edges {
										cursor
										node {
											id
											issueIds
											deploymentSequenceNumber
											updateSequenceNumber
											displayName
											url
											description
											state
											lastUpdated
											pipeline {
												url
												displayName
											}
											environment {
												category
												displayName
											}
										}
									}
								}
							}
						}
					`,
					{
						...variables,
					},
					{ fetchPolicy: 'store-or-network' },
				).toPromise();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				error = e;
			}
			// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'mainDeploymentsQuery$data | undefined'.
			const deploymentsPageResponse = transformDeploymentQueryResult(data, error);

			if (
				deploymentsPageResponse.loadingState === HAS_ERROR ||
				deploymentsPageResponse.data == null
			) {
				throw new Error(
					`Fetching deployment failed when fetching from ${i} to ${lastIndex}. Detail: ${
						deploymentsPageResponse?.error?.message ?? 'unknown'
					}`,
				);
			}
			const fetchedDeployments = deploymentsPageResponse.data;
			if (isInitialLoad()) {
				deploymentPageFetched.stop({
					customData: {
						...collectorFromNumIssuesDeployments({
							numIssues: partialIssueAris.length,
							numDeployments: fetchedDeployments.length,
							issuePageSize: ISSUE_PAGE_SIZE,
							deploymentsPageSize: DEPLOYMENTS_PAGE_SIZE,
						}),
						relay: true,
					},
				});
			}

			deployments = deployments.concat(fetchedDeployments);
			// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'mainDeploymentsQuery$data | undefined'.
			deploymentAfterCursor = getNextDeploymentPageCursor(data);
			const hasNextPage = !!deploymentAfterCursor;
			if (
				deploymentsPageNumber === MAXIMUM_DEPLOYMENT_PAGES_TO_FETCH_PER_ISSUE_PAGE &&
				hasNextPage
			) {
				hasReachedMaxDeploymentsLimit = true;
			}
		} while (!(deploymentAfterCursor == null || hasReachedMaxDeploymentsLimit));
	}

	if (isInitialLoad()) {
		deploymentsFetched.stop({
			customData: {
				...collectorFromNumIssuesDeployments({
					numIssues: issueIds.length,
					numDeployments: deployments.length,
					issuePageSize: ISSUE_PAGE_SIZE,
					deploymentsPageSize: DEPLOYMENTS_PAGE_SIZE,
				}),
				relay: true,
			},
		});
	}

	return {
		deployments: uniqBy(deployments, (deployment: Deployment) => deployment.id),
		hasReachedMaxDeploymentsLimit,
	};
};

const toDeploymentFilter = (
	{ environments, timeWindow }: Filter,
	issues: Issue[],
): DeploymentFilter => ({
	issueIds: issues.map((issue) => issue.id),
	environments,
	timeWindow,
});

const toIssueFilter = (
	{
		text,
		assignees,
		environments,
		timeWindow,
		epicIds,
		issueTypeIds,
		fixVersions,
		showIssuesWithoutDeployments,
	}: Filter,
	projectId: ProjectId,
): IssueFilter => ({
	projectId,
	epicIds,
	issueTypeIds,
	text,
	assignees,
	environments,
	fixVersions,
	timeWindow,
	showIssuesWithoutDeployments,
});

const shouldUseAGSDataFetching = (filter: Filter) =>
	ff('use-ags-for-deployments-timeline_22xgj') && isFilterSuitableForAGSQuery(filter);

export const fetchCoreData = async (
	environment: Environment,
	filter: Filter,
	totalIssuePageNum: number,
	projectId: ProjectId,
	cloudId: CloudId,
): Promise<FetchCoreDataResult | null> => {
	if (shouldUseAGSDataFetching(filter)) {
		return fetchCoreDataAGS(environment, filter, totalIssuePageNum, projectId, cloudId);
	}
	return fetchCoreDataAGSFFOff(environment, filter, totalIssuePageNum, projectId, cloudId);
};

export const fetchCoreDataAGS = async (
	environment: Environment,
	filter: Filter,
	totalIssuePageNum: number,
	projectId: ProjectId,
	cloudId: CloudId,
): Promise<FetchCoreDataResult | null> => {
	const fetchDeploymentsResult = await fetchDeploymentsAGS(
		environment,
		projectId,
		cloudId,
		totalIssuePageNum,
		filter,
	);
	if (fetchDeploymentsResult === null) {
		return null;
	}
	if (fetchDeploymentsResult.issueIds.length === 0) {
		// If there are no issues returned by the deployment query, there is nothing to display so
		// we can return an empty result here.
		return {
			coreData: {
				allDeployments: [],
				deploymentsByIssueId: {},
				issuesByIssueId: {},
				issues: [],
			},
			hasNextIssuePage: false,
			hasReachedMaxDeploymentsLimit: false,
		};
	}
	const fetchIssuesResult = await fetchIssuesAGS(
		environment,
		totalIssuePageNum,
		fetchDeploymentsResult.issueIds,
		cloudId,
		projectId,
	);
	if (fetchIssuesResult === null) {
		return null;
	}

	const { issues } = fetchIssuesResult;
	const { deployments } = fetchDeploymentsResult;
	const sortedDeployments = getOrderedDeploymentsByTime(deployments);
	const deploymentsByIssueId = groupDeploymentsByIssueId(sortedDeployments);
	const issuesByIssueId = keyBy(issues, 'id');
	const issuesOrderedNoDeploymentsLast = sortIssueByLatestDeploymentsAndLocateEmptyDeploymentsLast(
		issues,
		deploymentsByIssueId,
	);

	return {
		coreData: {
			allDeployments: sortedDeployments,
			deploymentsByIssueId,
			issuesByIssueId,
			issues: issuesOrderedNoDeploymentsLast,
		},
		/**
		 * We can try to fetch a new page when we scroll down if we either:
		 *
		 * - have more deployments we can fetch (fetchDeploymentsResult.hasNextPage) OR
		 * - there are more issueIds that haven't been fetched yet (fetchIssuesResult.hasNextPage)
		 */
		hasNextIssuePage: fetchDeploymentsResult.hasNextPage || fetchIssuesResult.hasNextPage,
		hasReachedMaxDeploymentsLimit: fetchDeploymentsResult.hasReachedMaxDeploymentsLimit,
	};
};

/**
 * What this function does
 *
 * 1. Fetch issues by iterating through issue pages up to totalIssuePageNum. After this step, we have a list of issues.
 * 2. For the given list of issues, iterate through each page of issues and fetch deployments for the page of issues.
 *    Deployment fetching itself is paginated by DEPLOYMENTS_PAGE_SIZE. This will accumulate all the deployments
 * 3. Return result: allDeployments, deploymentsByIssueId, issues
 *
 * If issue/deployment result is old, because there is more recent request being made, then this will return null to be ignored.
 *
 * As long as filter/list of issues are same, it will hit Apollo cache. Therefore it is okay to call this function multiple times.
 *
 * @param totalIssuePageNum     1-based number. If 3, it will fetch ISSUE_PAGE_SIZE * 3 issues.
 */
export const fetchCoreDataAGSFFOff = async (
	environment: Environment,
	filter: Filter,
	totalIssuePageNum: number,
	projectId: ProjectId,
	cloudId: CloudId,
): Promise<FetchCoreDataResult | null> => {
	const issueFilter = toIssueFilter(filter, projectId);
	const fetchIssuesResult = await fetchIssues(environment, issueFilter, totalIssuePageNum, cloudId);
	if (fetchIssuesResult === null) {
		return null;
	}

	const { issues, hasNextPage: hasNextIssuePage } = fetchIssuesResult;

	const deploymentFilter = toDeploymentFilter(filter, issues);
	const fetchDeploymentsResult = await fetchDeployments(environment, deploymentFilter, cloudId);
	if (fetchDeploymentsResult === null) {
		return null;
	}

	const { deployments, hasReachedMaxDeploymentsLimit } = fetchDeploymentsResult;
	const sortedDeployments = getOrderedDeploymentsByTime(deployments);
	const deploymentsByIssueId = groupDeploymentsByIssueId(sortedDeployments);
	const issuesByIssueId = keyBy(issues, 'id');
	const issuesOrderedNoDeploymentsLast = sortIssueByLatestDeploymentsAndLocateEmptyDeploymentsLast(
		issues,
		deploymentsByIssueId,
	);

	return {
		coreData: {
			allDeployments: sortedDeployments,
			deploymentsByIssueId,
			issuesByIssueId,
			issues: issuesOrderedNoDeploymentsLast,
		},
		hasNextIssuePage,
		hasReachedMaxDeploymentsLimit,
	};
};

const canFetchIssuePage = (numIssuePages: number): boolean =>
	ISSUE_PAGE_SIZE * numIssuePages <= MAXIMUM_FETCHABLE_ISSUES;

const hasMaximumFetchableIssuesReached = (numIssuePages: number): boolean =>
	ISSUE_PAGE_SIZE * numIssuePages >= MAXIMUM_FETCHABLE_ISSUES;

const constructResult = (
	coreData: CoreData | null,
	loadingState: LoadingState,
	error: Error | null,
	numIssuePages: number,
	fetchMore: () => void,
	hasNextIssuePage: boolean,
	hasReachedMaxDeploymentsLimit: boolean,
): CoreDataResult => {
	const getNextIssuePage =
		hasNextIssuePage && canFetchIssuePage(numIssuePages + 1) ? fetchMore : null;

	const hasReachedMaxIssuesLimit =
		hasMaximumFetchableIssuesReached(numIssuePages) && hasNextIssuePage;

	return {
		serviceResult: {
			error: error ?? undefined,
			loadingState,
			data: coreData ?? undefined,
		},
		getNextIssuePage,
		hasReachedMaxIssuesLimit,
		hasReachedMaxDeploymentsLimit,
	};
};
type CoreDataServiceProviderInputs = {
	children: ReactNode;
};

/**
 * @param filter    Null filter will be passed if filter is not ready (information is being fetched)
 */
export const CoreDataServiceProvider = ({ children }: CoreDataServiceProviderInputs) => {
	const filter = useCoreDataFilter();
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// RLSS-501: Move project ID into `Filter` type
	const projectId = useProjectId();
	const [
		{
			coreData,
			error,
			loadingState,
			numIssuePages,
			hasNextIssuePage,
			hasReachedMaxDeploymentsLimit,
		},
		dispatch,
		// @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
	] = useReducer<CoreDataState, CoreDataActions>(coreDataReducer, initialState);

	useEffect(() => {
		let wasCancelled = false;

		const fetchPaginationChunk = async () => {
			if (filter === null || projectId === null) {
				return;
			}
			try {
				// @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
				dispatch({ type: 'fetchTriggered' });
				if (isInitialLoad()) {
					issuesAndDeploymentsFetched.start();
				}
				const fetchCoreDataResult = await fetchCoreData(
					environment,
					filter,
					numIssuePages,
					projectId,
					cloudId,
				);
				if (fetchCoreDataResult === null || wasCancelled) {
					return;
				}
				const isFetchingViaAGS =
					ff('use-ags-for-deployments-timeline_22xgj') &&
					filter &&
					isFilterSuitableForAGSQuery(filter);
				if (isInitialLoad()) {
					issuesAndDeploymentsFetched.stop({
						customData: {
							...collectorFromCoreData({
								coreData: fetchCoreDataResult.coreData,
								issuePageSize: shouldUseAGSDataFetching(filter) ? ISSUE_PAGE_SIZE : ISSUE_PAGE_SIZE,
								deploymentsPageSize: DEPLOYMENTS_PAGE_SIZE,
							}),
							relay: true,
							isFetchingViaAGS,
						},
					});
					setInitialLoadFinished();
				}
				try {
					fireTrackAnalytics(createAnalyticsEvent({}), 'queried deploymentsTimelineData', {
						hasIssuesWithoutDeployments:
							Object.keys(fetchCoreDataResult.coreData.deploymentsByIssueId).length !==
							fetchCoreDataResult.coreData.issues.length,
						showIssuesWithoutDeployments: filter.showIssuesWithoutDeployments,
						hasFiltersApplied: isFilterApplied(filter),
					});
				} catch (e) {
					// Don't do anything
				}

				// @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
				dispatch({ type: 'fetchSuccess', payload: fetchCoreDataResult });
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				if (wasCancelled) {
					return;
				}
				// @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
				dispatch({ type: 'fetchFailed', payload: e });
			}
		};

		fetchPaginationChunk();

		return () => {
			wasCancelled = true;
		};
	}, [filter, numIssuePages, cloudId, environment, projectId, createAnalyticsEvent]);
	useEffect(() => {
		// @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
		dispatch({ type: 'filterChanged' });
	}, [filter]);

	const fetchMore = () => {
		// @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
		dispatch({ type: 'fetchMore' });
	};
	return (
		<CoreDataContext.Provider
			value={constructResult(
				coreData,
				loadingState,
				error,
				numIssuePages,
				fetchMore,
				hasNextIssuePage,
				hasReachedMaxDeploymentsLimit,
			)}
		>
			{children}
		</CoreDataContext.Provider>
	);
};

export const useEarliestVisibleDeploymentDate = (): Date | null => {
	const { serviceResult } = useCoreData();
	return getEarliestVisibleDeploymentDate(serviceResult);
};

export const getEarliestVisibleDeploymentDate = (
	serviceResult: ServiceQueryResultWithStatus<CoreData>,
): Date | null => {
	const issues = serviceResult.data?.issues ?? [];
	const deploymentsByIssueId = serviceResult.data?.deploymentsByIssueId ?? {};
	// Note: the "issues without deployments" setting means it's possible for
	// `deploymentsByIssueId[issue.id]` to yield no result.
	const issueDeploymentDates = issues
		.flatMap((issue) => deploymentsByIssueId[issue.id] ?? [])
		.map((deployment) => deployment.lastUpdated);
	return issueDeploymentDates.length === 0 ? null : min(issueDeploymentDates);
};
